import { CardActionArea } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid2 from '@mui/material/Grid2';
import { color } from '@/colors';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { toMillis } from '@/utils/folder.utils';
import { I18N_KEYS } from '@/language/keys';
import { useUserStore } from '@/state/user.state';
import { goToRecipe } from '@/utils/recipes';
import { DashboardWorkflow } from '@/types/workflows';
import { FlexCol } from '@/UI/styles';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { DashboardCard } from './DashboardCard';
import { DashboardInlineEditInput } from './DashboardInlineEditInput';
import WorkflowCardDisplay from './components/WorkflowCardDisplay';
import type { EditingNameState } from './DashboardMain';

interface WorkflowCardProps {
  workflow: DashboardWorkflow & { owner?: string; ownerName?: string };
  workflowEditState: EditingNameState;
  handleContextMenu: (e: React.MouseEvent<HTMLDivElement>, id: string, itemType?: 'folder' | 'recipe') => void;
  handleRenameWorkflow: (id: string, name: string) => void;
  handleCancelRenameWorkflow: () => void;
  isDragging?: boolean;
  onDragStart?: (e: React.DragEvent, itemId: string) => void;
  onDragEnd?: () => void;
  showOwner?: boolean;
}

export const WorkflowCard = ({
  workflow,
  workflowEditState,
  handleContextMenu,
  handleRenameWorkflow,
  handleCancelRenameWorkflow,
  isDragging = false,
  onDragStart,
  onDragEnd,
  showOwner = false,
}: WorkflowCardProps) => {
  const [editingValue, setEditingValue] = useState(workflow.name);

  const { t } = useTranslation();
  const currentUser = useUserStore((state) => state.user);
  const isEditing = workflowEditState.kind === 'editing' && workflowEditState.id === workflow.id;

  useEffect(() => {
    if (isEditing) {
      setEditingValue(workflow.name);
    }
  }, [isEditing, workflow.name]);

  const handleClick = () => {
    if (!isEditing) {
      goToRecipe(workflow.id);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isEditing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editingValue.trim()) {
        handleRenameWorkflow(workflow.id, editingValue);
      } else {
        handleCancelRenameWorkflow();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRenameWorkflow();
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
      <DashboardCard
        data-testid={`recipe-card-${workflow.id}`}
        onContextMenu={(e) => {
          e.stopPropagation();
          handleContextMenu(e, workflow.id, 'recipe');
        }}
        draggable={!isEditing}
        onDragStart={(e) => onDragStart?.(e, workflow.id)}
        onDragEnd={onDragEnd}
        sx={{
          cursor: isEditing ? 'default' : isDragging ? 'grabbing' : 'grab',
        }}
      >
        <CardActionArea
          disableRipple
          sx={{ position: 'relative', '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 } }}
          onClick={handleClick}
        >
          <WorkflowCardDisplay poster={workflow.poster} />
        </CardActionArea>
        <FlexCol sx={{ pl: 1, pt: 1, pb: 3, pr: 2 }}>
          <DashboardInlineEditInput
            isEditing={isEditing}
            value={editingValue}
            displayValue={workflow.name}
            onChange={setEditingValue}
            onKeyDown={onKeyDown}
            onBlur={handleCancelRenameWorkflow}
            data-testid={`recipe-title-${workflow.id}`}
          />
          <TextSmRg sx={{ color: color.White64_T }}>
            {showOwner
              ? currentUser && workflow.owner && workflow.owner === currentUser.uid
                ? t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.OWNED_BY_YOU)
                : t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.OWNED_BY_OTHER, {
                    owner: workflow.ownerName || '-',
                  })
              : workflow.updatedAt
                ? t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_LAST_EDITED, {
                    date: getTimeDiffInWords(toMillis(workflow.updatedAt)),
                  })
                : t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_CREATED, {
                    date: getTimeDiffInWords(toMillis(workflow.createdAt)),
                  })}
          </TextSmRg>
        </FlexCol>
      </DashboardCard>
    </Grid2>
  );
};
